import { useMutation } from '@apollo/client/react';
import { Duration } from '@js-joda/core';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { Loading } from 'react-admin';

import {
  type AdminLaunchClientSetupFragment,
  AdminLaunchUpdateClientDocument,
} from '../../../graphql/types';
import { FieldName } from './FieldNames';
import {
  ChoiceCard,
  ChoiceCards,
  CollapsedSettings,
  parseWholeNumber,
  ReloadAlert,
  Section,
  SettingSwitch,
} from './fields';
import type { StepProps } from './types';
import { useStepSetup } from './useStepSetup';

const toMinutes = (duration: unknown) =>
  typeof duration === 'string' && duration
    ? String(Duration.parse(duration).toMinutes())
    : '';

const toText = (value: number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

/** Blank is no value; anything else must be a whole number. */
const parseOptional = (value: string) =>
  value.trim() ? parseWholeNumber(value) : undefined;

const NumberField: React.FC<{
  label: string;
  value: string;
  fieldName: string;
  helperText?: string;
  error: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, fieldName, helperText, error, onChange }) => (
  <Box>
    <TextField
      size="small"
      fullWidth
      label={label}
      inputMode="numeric"
      value={value}
      error={error}
      helperText={
        error ? 'Enter a whole number, or leave it blank.' : helperText
      }
      onChange={(event) => onChange(event.target.value)}
    />
    <FieldName>{fieldName}</FieldName>
  </Box>
);

const SsoDetection: React.FC<{
  domains: string[];
  eligFile: boolean;
}> = ({ domains, eligFile }) => {
  let content: React.ReactNode;
  if (domains.length) {
    content = (
      <Stack spacing={0.5}>
        {domains.map((domain) => (
          <Stack key={domain} direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {domain}
            </Typography>
            <Chip size="small" variant="outlined" label="Checked in Auth0" />
          </Stack>
        ))}
      </Stack>
    );
  } else if (eligFile) {
    content = (
      <Typography variant="body2">
        Auth0 checks the email domains found in the eligibility file.
      </Typography>
    );
  } else {
    content = (
      <Typography variant="body2">
        No domains or eligibility file to check. Enter and save the Auth0
        connection name under Additional settings.
      </Typography>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="caption" color="text.disabled" fontWeight={600}>
        How SSO is detected
      </Typography>
      {content}
    </Paper>
  );
};

const SignInForm: React.FC<
  StepProps & {
    client: AdminLaunchClientSetupFragment;
    /** Set when the latest reload failed; saving waits for a successful one. */
    reloadError: string | undefined;
  }
> = ({
  client,
  reloadError,
  choices,
  onChoicesChange,
  onEdit,
  registerSave,
}) => {
  const [usesSSO, setUsesSSO] = React.useState(choices.usesSSO);
  const [connection, setConnection] = React.useState(
    client.ssoIdentity?.connection ?? '',
  );
  const [strictAppCheck, setStrictAppCheck] = React.useState(
    client.strictAppCheck,
  );
  const [globalSignOut, setGlobalSignOut] = React.useState(
    client.usesGlobalSignOut,
  );
  const [fixNames, setFixNames] = React.useState(
    client.needsUsToFixTheirUserNames,
  );
  const [biometrics, setBiometrics] = React.useState(client.biometricsEnabled);
  const [hardSignOutDays, setHardSignOutDays] = React.useState(
    toText(client.biometricHardSignOutDuration),
  );
  const [biometricTimeout, setBiometricTimeout] = React.useState(
    toText(client.biometricInactivityTimeout),
  );
  const [inactivity, setInactivity] = React.useState(
    toMinutes(client.inactivityDuration),
  );
  const [showErrors, setShowErrors] = React.useState(false);
  const [updateClient] = useMutation(AdminLaunchUpdateClientDocument);

  // Domains only exist for "Email domain only", and they're saved by then.
  const domains = choices.eligDomainOnly
    ? client.domains.nodes.map((node) => node.domain)
    : [];
  const needsConnection = !domains.length && !choices.eligFile;

  // The durations are hidden while biometrics is off, so a bad value there
  // can't block the save; it's dropped instead.
  const biometricNumber = (value: string) => {
    const parsed = parseOptional(value);
    return biometrics ? parsed : (parsed ?? undefined);
  };
  const numbers = {
    hardSignOutDays: biometricNumber(hardSignOutDays),
    biometricTimeout: biometricNumber(biometricTimeout),
    inactivity: parseOptional(inactivity),
  };
  const errors = {
    connection:
      usesSSO && needsConnection && !connection.trim()
        ? 'Enter the Auth0 connection name from engineering.'
        : undefined,
    numbers: Object.values(numbers).includes(null)
      ? 'Fix the numbers under Additional settings.'
      : undefined,
  };
  const firstError = errors.connection ?? errors.numbers;

  // Registration through a connection needs it saved, and the step only
  // saves once registration is confirmed, so it can be saved on its own.
  const [savingConnection, setSavingConnection] = React.useState(false);
  const [connectionSaveError, setConnectionSaveError] = React.useState<
    string | undefined
  >();
  const connectionSaved =
    connection.trim() === (client.ssoIdentity?.connection ?? '');
  const handleSaveConnection = async () => {
    setSavingConnection(true);
    try {
      await updateClient({
        variables: {
          input: { id: client.id, ssoConnection: connection.trim() || null },
        },
      });
    } catch (error) {
      setConnectionSaveError(
        `Failed to save: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setSavingConnection(false);
    }
  };

  const handleMethod = (sso: boolean) => {
    setUsesSSO(sso);
    onChoicesChange?.({ usesSSO: sso });
    onEdit();
  };

  React.useEffect(() => {
    if (!registerSave) {
      return;
    }
    registerSave(async () => {
      if (reloadError) {
        throw new Error(
          "The latest sign-in settings didn't load, so they can't be saved. Try loading them again.",
        );
      }
      if (firstError) {
        setShowErrors(true);
        throw new Error(firstError);
      }
      const inactivityMinutes = numbers.inactivity ?? 0;
      await updateClient({
        variables: {
          input: {
            id: client.id,
            // A connection is what makes a client SSO without domains, so
            // email and password clears it.
            ssoConnection: usesSSO ? connection.trim() || null : null,
            usesGlobalSignOut: usesSSO && globalSignOut,
            needsUsToFixTheirUserNames: usesSSO && fixNames,
            strictAppCheck,
            biometricsEnabled: biometrics,
            biometricHardSignOutDuration: numbers.hardSignOutDays ?? null,
            biometricInactivityTimeout: numbers.biometricTimeout ?? null,
            inactivityDuration: inactivityMinutes
              ? Duration.ofMinutes(inactivityMinutes).toString()
              : null,
          },
        },
      });
    });
    return () => registerSave(undefined);
  }, [
    reloadError,
    registerSave,
    firstError,
    numbers.hardSignOutDays,
    numbers.biometricTimeout,
    numbers.inactivity,
    client.id,
    usesSSO,
    connection,
    globalSignOut,
    fixNames,
    strictAppCheck,
    biometrics,
    updateClient,
  ]);

  const settingsOn =
    [strictAppCheck, biometrics, Boolean(inactivity.trim())].filter(Boolean)
      .length +
    (usesSSO
      ? [globalSignOut, fixNames, Boolean(connection.trim())].filter(Boolean)
          .length
      : 0);

  const toggle = (set: (value: boolean) => void) => (checked: boolean) => {
    set(checked);
    onEdit();
  };
  const number = (set: (value: string) => void) => (value: string) => {
    set(value);
    onEdit();
  };

  return (
    <Stack spacing={3}>
      <Section title="How members sign in">
        <RadioGroup
          aria-label="Sign-in method"
          value={usesSSO ? 'sso' : 'password'}
          onChange={(event) => handleMethod(event.target.value === 'sso')}
        >
          <ChoiceCards>
            <ChoiceCard
              value="sso"
              checked={usesSSO}
              control={<Radio />}
              title="Single sign-on"
              tag={{ label: 'Recommended', recommended: true }}
              description="Members use their work login through the client's identity provider (Okta, Entra ID, Google)."
            />
            <ChoiceCard
              value="password"
              checked={!usesSSO}
              control={<Radio />}
              title="Email & password"
              description="Members create an Exos account with their work email and a password."
            />
          </ChoiceCards>
        </RadioGroup>
        {usesSSO && (
          <SsoDetection domains={domains} eligFile={choices.eligFile} />
        )}
      </Section>

      <CollapsedSettings
        title="Additional settings"
        description="Leave these off unless the client specifically asks for them. Don't offer them to a client without checking with engineering first."
        inUse={settingsOn}
        inUseLabel="on"
        noneLabel="All off"
        forceOpen={showErrors && Boolean(firstError)}
      >
        {usesSSO && (
          <Box sx={{ maxWidth: 460 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                label="Auth0 connection name"
                placeholder="NORTHWIND-SAML"
                required={needsConnection}
                value={connection}
                error={
                  Boolean(connectionSaveError) ||
                  (showErrors && Boolean(errors.connection))
                }
                helperText={
                  connectionSaveError ??
                  (showErrors && errors.connection
                    ? errors.connection
                    : needsConnection
                      ? 'Required: there are no domains or file for Auth0 to check. Save it to test registration.'
                      : 'Only if engineering gives you one, e.g. members sign up with personal emails. Must be unique across clients.')
                }
                onChange={(event) => {
                  setConnection(event.target.value);
                  setConnectionSaveError(undefined);
                  onChoicesChange?.({
                    ssoConnection: event.target.value.trim(),
                  });
                  onEdit();
                }}
              />
              <Button
                variant="outlined"
                sx={{ flexShrink: 0, height: 40 }}
                disabled={
                  connectionSaved || savingConnection || Boolean(reloadError)
                }
                onClick={handleSaveConnection}
              >
                {connectionSaved && connection.trim() ? 'Saved' : 'Save'}
              </Button>
            </Stack>
            <FieldName>clients.ssoConnection</FieldName>
          </Box>
        )}
        <SettingSwitch
          label="Block authentication requests from outside the app"
          description="Detects jailbroken devices and scripted sign-ins."
          fieldName="clients.strictAppCheck"
          checked={strictAppCheck}
          onChange={toggle(setStrictAppCheck)}
        />
        {usesSSO && (
          <SettingSwitch
            label="Global sign-out"
            description="Signing out of the app also ends the member's SSO session with the client's identity provider, on every device."
            fieldName="clients.usesGlobalSignOut"
            checked={globalSignOut}
            onChange={toggle(setGlobalSignOut)}
          />
        )}
        {usesSSO && (
          <SettingSwitch
            label="Force capitalization of people's names"
            description="Use if the client's identity provider sends BRAD instead of Brad."
            fieldName="clients.needsUsToFixTheirUserNames"
            checked={fixNames}
            onChange={toggle(setFixNames)}
          />
        )}
        <SettingSwitch
          label="Enable biometric authentication"
          fieldName="clients.biometricsEnabled"
          checked={biometrics}
          onChange={toggle(setBiometrics)}
        />
        {biometrics && (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            }}
          >
            <NumberField
              label="Hard sign-out duration (days)"
              helperText="Blank uses the default, 28 days."
              value={hardSignOutDays}
              fieldName="clients.biometricHardSignOutDuration"
              error={showErrors && numbers.hardSignOutDays === null}
              onChange={number(setHardSignOutDays)}
            />
            <NumberField
              label="Biometric inactivity timeout (minutes)"
              helperText="Blank uses the default, 30 minutes."
              value={biometricTimeout}
              fieldName="clients.biometricInactivityTimeout"
              error={showErrors && numbers.biometricTimeout === null}
              onChange={number(setBiometricTimeout)}
            />
          </Box>
        )}
        <Box sx={{ maxWidth: 360 }}>
          <NumberField
            label="Require sign-in again after N minutes idle"
            helperText="Blank means no limit."
            value={inactivity}
            fieldName="clients.inactivityDuration"
            error={showErrors && numbers.inactivity === null}
            onChange={number(setInactivity)}
          />
        </Box>
      </CollapsedSettings>
    </Stack>
  );
};

/** SSO or email and password, plus rarely used security settings. */
export const SignInStep: React.FC<StepProps> = (props) => {
  const { launch, registerSave } = props;
  const { setup, firstLoad, data, reloadError, retry } = useStepSetup(
    launch?.client.id,
    registerSave,
    'sign-in settings',
  );

  if (!launch) {
    return null;
  }
  if (firstLoad) {
    return <Loading />;
  }
  if (!data) {
    return (
      <Alert severity="error">
        {setup.error?.message ?? "The sign-in settings didn't load."}
      </Alert>
    );
  }
  const client = data.client;
  if (!client) {
    return <Alert severity="warning">This client doesn't exist.</Alert>;
  }
  return (
    <Stack spacing={2}>
      {reloadError && (
        <ReloadAlert
          settingsName="sign-in settings"
          message={reloadError}
          onRetry={retry}
        />
      )}
      <SignInForm {...props} client={client} reloadError={reloadError} />
    </Stack>
  );
};
